import _filter from 'lodash/filter';
import _difference from 'lodash/difference';
import _differenceBy from 'lodash/differenceBy';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _now from 'lodash/now';
import _size from 'lodash/size';
import _toInteger from 'lodash/toInteger';
import _toString from 'lodash/toString';
import _unset from 'lodash/unset';
import _split from 'lodash/split';
import _keyBy from 'lodash/keyBy';

import EventBus from '@/event-bus';
import isSafari from '@/helpers/isSafari';
import Shop from '@/shop';
import createStorage from '@/storage';
import Promise from 'promise';
import ajax from '@/ajax/product';
import getCacheLifeTime from '@/products/cache-life-time';
import perfDebug from '@/helpers/perfDebug';

const currency = Shop.config.config.currency_code;
const Store = createStorage({ name: 'Products-' + currency });
const lifeTime = getCacheLifeTime();

/**
 * @private
 * @class
 * @description
 * Класс Хранилища json Товаров
 * управляет всем процессом получения и хранения json'ов
 *
 * @return {Object} - instance
 */
const ProductsStorage = function() {
  const self = this;
  const clearCache = Shop.config.get('clear_cache').clear_cache;

  self.lifeTime = getCacheLifeTime();

  // Флаг для соблюдения очереди запросов на получение товаров
  self.pauseAjax = false;

  // Флаг для соблюдения очереди запросов на сохранение товаров в Storage
  self.isSaving = false;

  if (clearCache) {
    Store.clear();
  }

  EventBus.subscribe('update:client', function() {
    // Возможно обновился тип цены для клиента
    Store.clear();
  });

  return self;
};

/**
 * @description
 * Получаем товары из Storage, проверяем их актуальность. Если товары не актуальны, то запрашиваем их с сервера, затем снова сохраняем в Storage и возвращаем. Здесь ключевой момент в том, что мы отталкиваемся от того, нашли мы товары в Storage или нет. Если не нашли, или они записались после того как мы начали их искать, то будет много лишних запрсоов к серверу. Отсюда необходимость в паузе.
 *
 * @param {Array} idList - массив id товаров, информация по которым нам нужнв
 * @param {Object} options - Объект с настройками
 *
 * @return {Object} - Объект с товарами
 */
ProductsStorage.prototype.getProducts = async function(idList, options = {}) {
  try {
    const self = this;
    const locale = Shop.config.get('locale').locale;
    const ids = _split(_toString(idList), ',');

    perfDebug.log('storage:getProducts:enter', {
      ids: ids.length,
      noCache: Boolean(options.noCache),
      withoutManyVariants: Boolean(options.withoutManyVariants),
      pauseAjax: self.pauseAjax,
    });

    // Проверяем соответствует ли текущая локаль из конфига локали в Storage. Если нет, то очищаем Storage и добавляем её
    const currentLocale = await Store.getItem('locale');
    if (currentLocale !== locale) {
      try {
        await Store.clear();
      } catch (err) {
        console.warn('Error in Products.Store: getProducts: ', err);
      }
      await Store.setItem('locale', locale);
    }

    // Если запрос на получение товаров уже в идёт, то ждём его завершения и сохранения товаров в Storage перед тем как продолжить
    if (self.pauseAjax) {
      await new Promise(resolve => setTimeout(resolve, 200));

      return self.getProducts(idList, options);
    }

    // Ставим ожидание для последующих вызовов
    self.pauseAjax = true;

    // Получаем товары из Storage
    let storedProducts = await Store.getItems(ids);

    // Проверяем актуальность товаров из Storage
    const checkedProducts = self._checkAlive(storedProducts, options);

    // Сравниваем актуальные товары из Storage со списком тех которые прислали. Получаем список тех, которых в Storage не оказалось
    const idToUpload = _differenceBy(idList, _map(_keys(checkedProducts), _toInteger));

    // Если мы нашли в Storage необходимые товары, то просто возвращаем их
    if (!idToUpload.length) {
      self.pauseAjax = false;

      perfDebug.log('storage:getProducts:cacheOnly', {
        returned: _keys(checkedProducts).length,
      });

      return checkedProducts;
    }

    perfDebug.log('storage:getProducts:needNetwork', {
      idToUpload: idToUpload.length,
      fromCache: _keys(checkedProducts).length,
    });

    // Получаем недостающие товары с сервера
    const products = await new Promise((resolve, reject) => {
      ajax.getList(idToUpload, {
        noCache: options.noCache || false,
        isCommonRequest: true,
        withoutManyVariants: options.withoutManyVariants,
        cartIdsMap: options.cartIdsMap
      }).done(async (_products) => {
        const deleted = _difference(idToUpload, _map(_products, 'id'));
        if (_size(deleted) > 0) {
          EventBus.publish('products-storage-deleted:insales:common', deleted);
        }

        resolve(_products);
      }).fail(reject);
    });

    perfDebug.log('storage:getProducts:ajaxReturned', {
      products: _size(products),
    });

    // Сохраняем товары в Storage
    await self.saveProducts(products, options);

    // Получаем необходимые товары из Storage
    storedProducts = await Store.getItems(ids);

    // Убираем паузу и возвращаем объект с товарами
    self.pauseAjax = false;

    return storedProducts;
  } catch (err) {
    self.pauseAjax = false;
    console.warn('Error in Products.Store: getProducts: ', err);

    return {};
  }
};

/**
 * Универсальный метод для сохранения товаров в Storage
 * @param {Array} products - массив с объектами товаров
 * @param {Boolean} options - опции для разных кейсов
 *
 *
 * @return {Promise} - возвращаем промис
 */
ProductsStorage.prototype.saveProducts = async function(products, options = {}) {
  const self = this;

  if (!products || !Object.values(products).length) {
    return;
  }

  // Так как метод может быть вызван откуда угодно, то могут быть параллельные вызовы, которые нужно обработать в порядке очереди
  if (self.isSaving) {
    await new Promise(resolve => setTimeout(resolve, 200));

    return self.saveProducts(products, options);
  }

  self.isSaving = true;

  perfDebug.log('storage:saveProducts:enter', {
    items: Object.values(products).length,
    checkLocale: Boolean(options.checkLocale),
  });

  return new Promise(async (resolve) => {
    const productsOriginalUpdatedAt = window.Products.productsOriginalUpdatedAt;
    let currentMoment = _now();
    let productsObject = {};

    if (options.checkLocale) {
      // Проверяем есть ли локаль, если нет, то очищаем Storage и добавляем её
      const locale = Shop.config.get('locale').locale;
      const storeCurrentLocale = await Store.getItem('locale');

      if (storeCurrentLocale !== locale) {
        await Store.clear();
        await Store.setItem('locale', locale);
      }
    }

    const productList = Array.isArray(products) ? products : Object.values(products);
    const productIdsArray = _map(productList, (product) => _toString(product.id));
    let currentItems = await Store.getItems(productIdsArray);

    const productsToSave = _filter(productList, function(product) {
      const idStr = _toString(product.id);
      const cur = currentItems[idStr];

      if (
        cur &&
        productsOriginalUpdatedAt &&
        productsOriginalUpdatedAt.has(product.id) &&
        cur.originalUpdatedAt === productsOriginalUpdatedAt.get(product.id) &&
        Array.isArray(cur.variants) &&
        Array.isArray(product.variants) &&
        cur.variants.length === product.variants.length
      ) {
        perfDebug.log('storage:saveProducts:skipUnchanged', {
          productId: product.id,
          reason: 'originalUpdatedAt+variantsLength',
        });

        return false;
      }

      return true;
    });

    if (!productsToSave.length) {
      self.isSaving = false;
      resolve();

      return;
    }

    function buildProductsObjectFromDom() {
      return _forEach(_keyBy(productsToSave, 'id'), product => {
        // Если мы точно знаем что получили эти товары с сервера (ui-ajax-products), то сразу сохраняем их реальные updatedAt в глобальный словарь
        if (options.withOriginalUpdatedAt) {
          const originalUpdatedAt = Math.round(new Date(product.updated_at).getTime() / 1000);

          productsOriginalUpdatedAt.set(product.id, originalUpdatedAt);
        }

        // Если при инициализации товара мы нашли его в DOM и получили originalUpdatedAt, то записываем его для товара в кэше, чтобы в будущем сравнивать
        if (productsOriginalUpdatedAt.has(product.id)) {
          product.originalUpdatedAt = productsOriginalUpdatedAt.get(product.id);
        }

        const nodesWithoutCache =
          Array.from(document.querySelectorAll(`[data-product-id="${product.id}"][data-product-without-cache]`));

        if (nodesWithoutCache.length) {
          product.updatedAt = currentMoment - lifeTime + 3000;

          return;
        }

        product.updatedAt = currentMoment;
      });
    }

    productsObject = perfDebug.isEnabled()
      ? perfDebug.timeSync('storage:saveProducts:domQueryPerProduct', buildProductsObjectFromDom)
      : buildProductsObjectFromDom();

    if (isSafari()) {
      window.indexedDB.deleteDatabase('safari-wake-up');
    }

    try {
      // Если товар был получен с параметром without_many_variants, то при его сохранении в Storage нужно не затереть варианты, если они там есть
      function mergeProductVariantBranch(key) {
        const currentItem = currentItems[key];
        const newItem = productsObject[key];

        if (!currentItem || !newItem) { return; }

        if (
          (options.withoutManyVariants || newItem.is_more_than_one_variant)
          && currentItem.variants.length > newItem.variants.length
        ) {
          const index = currentItem.variants.findIndex(variant => newItem.variants[0].id === variant.id);
          if (index !== -1) {
            currentItem.variants[index] = newItem.variants[0];

            currentItem.variants = currentItem.variants.slice(index);
          } else {
            currentItem.variants.unshift(newItem.variants[0]);
          }

          newItem.variants = currentItem.variants;
          newItem.is_more_than_one_variant = currentItem.is_more_than_one_variant;
        }

        // Если при добавлении в корзину товара у него столько же вариантов сколько у товара в кэше, при этом, на самом деле у товара вариантов больше, то прежде чем перезаписывать данные нужно проверить, что те варианты которые прилетели совпадают с теми вариантами, которые уже лежат в кэше, и если нет, то добавить их в массив
        if (
          newItem.is_more_than_one_variant
          && options.cartIdsMap
          && currentItem.variants.length === newItem.variants.length
        ) {
          currentItem.variants.forEach(variant => {
            if (newItem.variants[0].id !== variant.id) {
              newItem.variants.push(variant);
            }
          });
        }
      }

      if (perfDebug.isEnabled()) {
        perfDebug.timeSync('storage:saveProducts:mergeVariantsInMemory', function() {
          Object.keys(productsObject).forEach(key => {
            mergeProductVariantBranch(key);
          });
        });
      } else {
        Object.keys(productsObject).forEach(key => {
          mergeProductVariantBranch(key);
        });
      }

      Store.setItems(productsObject)
        .finally(() => {
          resolve();
          self.isSaving = false;
        });
    } catch (err) {
      self.isSaving = false;
      console.warn('Error in SaveProducts module: ', err);
    }
  });
};

/**
 * @private
 * @description
 * Проверяем актуальность товаров полученных из Storage.
 * Если в глобальном словаре productsOriginalUpdatedAt с реальными датами последнего обновления товаров есть проверяемый товар и его originalUpdatedAt совпадает с тем что у товара в кэше, то товар валидный. Если запрашивается товар с вариантами, а в кэше лежит без вариантов, то это невалидный товар.
 *
 * Валидными считаются товары которые мы запросили из корзины (cartIdsMap) и нашли их в кэше.
 * Во всех остальных случаях всё делаем как раньше, смотрим прошло ли определенное количество времени (0/3/60/120 секунд).
 *
 *
 * @param {Object} products - текущая информация о товарах
 * @param {Object} options - объект с настройками
 *
 * @return {Object} - актуальная информация
 */
ProductsStorage.prototype._checkAlive = function(products, options = {}) {
  const self = this;
  const currentMoment = _now();
  const productsOriginalUpdatedAt = window.Products.productsOriginalUpdatedAt;

  _forEach(products, function(product, key) {
    let isValid = null;

    if (product) {
      if (productsOriginalUpdatedAt.has(product.id)) {
        isValid = product.originalUpdatedAt === productsOriginalUpdatedAt.get(product.id);
      } else if (options.cartIdsMap && options.cartIdsMap.size) {
        isValid = true;
      } else {
        isValid = (currentMoment - product.updatedAt) < self.lifeTime;
      }

      // Если нам нужен товар с вариантами, а в кеше лежит без вариантов, то это невалидный товар
      if (!options.withoutManyVariants && !options.cartIdsMap && product.is_more_than_one_variant) {
        isValid = false;
        options.noCache = true;
      }

      // Если мы запрашиваем товары из корзины, то сравниваем запрашиваемые ID вариантов с ID вариантов у товара, чтобы убедиться что у товара в кэше лежат нужные варианты
      if (options.cartIdsMap) {
        Array.from(options.cartIdsMap.entries()).forEach(([ cartId, productId ]) => {
          if (productId !== product.id) {
            return;
          }

          if (!product.variants.map(variant => variant.id).includes(cartId)) {
            isValid = false;
            options.noCache = true;
          }
        });
      }
    } else {
      _unset(products, key);
    }

    if (!isValid) {
      _unset(products, key);
    }
  });

  return products;
};

export default ProductsStorage;

perfDebug.logModuleEval('@/products/storage');
